// ============================================================
// Fee Service
// ============================================================
// Scoped by schoolId to support multi-school architecture.

import { feeStructuresApi } from '@/api/feeStructures.api';
import { studentFeeAssignmentsApi } from '@/api/studentFeeAssignments.api';
import { feeDashboardApi } from '@/api/feeDashboard.api';
import { mockFeeStructures } from '@/mock/feeStructures';
import { mockTransactions } from '@/mock/transactions';
import { mockReceipts } from '@/mock/receipts';
import { mockPendingFees } from '@/mock/pendingFees';
import { MOCK_DELAY } from '@/constants/app';
import { generateId } from '@/utils/generateId';
import { numberToWords } from '@/utils/numberToWords';

const delay = (ms = MOCK_DELAY.MEDIUM) => new Promise((resolve) => setTimeout(resolve, ms));

const mockStudentFeeAssignments = {};

export const feeService = {
  // ── Fee Structures ──────────────────────────────────────────

  getFeeStructures: async (schoolId) => {
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    try {
      const data = await feeStructuresApi.getFeeStructures(schoolId);
      const list = Array.isArray(data) ? data : data?.data || [];
      return { success: true, data: list };
    } catch (err) {
      console.warn('API error fetching fee structures, using mock fallback:', err.message);
      return { success: true, data: mockFeeStructures[schoolId] || [] };
    }
  },

  getFeeStructureById: async (schoolId, id) => {
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    try {
      const res = await feeStructuresApi.getFeeStructureById(schoolId, id);
      return { success: true, data: res };
    } catch (err) {
      console.warn('API error fetching fee structure by id:', err.message);
      const list = mockFeeStructures[schoolId] || [];
      const found = list.find((item) => item.id === id);
      return { success: true, data: found };
    }
  },

  createFeeStructure: async (schoolId, data) => {
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    try {
      const res = await feeStructuresApi.createFeeStructure(schoolId, data);
      const createdId = res?.id || res?.data?.id || res?.structure_id || res?.data?.structure_id;
      return {
        ...res,
        success: true,
        id: createdId,
        structure_id: createdId,
        data: res?.data || res,
        message: res?.message || 'Successfully created new fee structure'
      };
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to create fee structure';
        throw new Error(errorMsg);
      }
      console.warn('API error creating fee structure, saving locally:', err.message);
      const newFee = {
        id: generateId('fee-str-'),
        is_active: true,
        is_default: true,
        created_at: new Date().toISOString(),
        ...data,
      };

      if (!mockFeeStructures[schoolId]) {
        mockFeeStructures[schoolId] = [];
      }
      mockFeeStructures[schoolId].push(newFee);
      return { success: true, id: newFee.id, data: newFee, message: 'Successfully created new fee structure' };
    }
  },

  updateFeeStructure: async (schoolId, id, data) => {
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    try {
      const res = await feeStructuresApi.updateFeeStructure(schoolId, id, data);
      return { success: true, data: res?.data || res, message: res?.message || 'Successfully updated fee structure' };
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to update fee structure';
        throw new Error(errorMsg);
      }
      console.warn('API error updating fee structure, updating locally:', err.message);
      const list = mockFeeStructures[schoolId] || [];
      const index = list.findIndex((item) => item.id === id);
      if (index !== -1) {
        list[index] = { ...list[index], ...data, updated_at: new Date().toISOString() };
      }
      return { success: true, data: list[index] };
    }
  },

  deleteFeeStructure: async (schoolId, id) => {
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    try {
      const res = await feeStructuresApi.deleteFeeStructure(schoolId, id);
      return { success: true, message: res?.message || 'Fee structure deleted successfully.' };
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to delete fee structure';
        throw new Error(errorMsg);
      }
      console.warn('API error deleting fee structure, deleting locally:', err.message);
      if (mockFeeStructures[schoolId]) {
        mockFeeStructures[schoolId] = mockFeeStructures[schoolId].filter((item) => item.id !== id);
      }
      return { success: true, message: 'Fee structure deleted successfully.' };
    }
  },

  // ── Fee Assignment ──────────────────────────────────────────

  getAssignableFeeStructures: async (schoolId, skip = 0, limit = 100) => {
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    try {
      const data = await studentFeeAssignmentsApi.getAssignableStructures(schoolId, skip, limit);
      const list = Array.isArray(data) ? data : data?.data || [];
      return { success: true, data: list };
    } catch (err) {
      console.warn('API error fetching assignable fee structures, using fee structures fallback:', err.message);
      return feeService.getFeeStructures(schoolId);
    }
  },

  getStudentFeeAssignments: async (schoolId, skip = 0, limit = 100) => {
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    try {
      const data = await studentFeeAssignmentsApi.getAssignments(schoolId, skip, limit);
      const list = Array.isArray(data) ? data : data?.data || [];
      return { success: true, data: list };
    } catch (err) {
      console.warn('API error fetching student fee assignments, using mock fallback:', err.message);
      return { success: true, data: mockStudentFeeAssignments[schoolId] || [] };
    }
  },

  assignStudentFees: async (schoolId, payload) => {
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    if (!payload?.student_ids?.length) throw { status: 400, message: 'Select at least one student' };
    if (!payload?.fee_structure_ids?.length) {
      throw { status: 400, message: 'Select at least one fee structure' };
    }

    try {
      const res = await studentFeeAssignmentsApi.createAssignment(schoolId, payload);
      return {
        success: true,
        data: res,
        message: 'Successfully assigned fee structures to selected students.',
      };
    } catch (err) {
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to assign fee structures';
      throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  },

  deleteStudentFeeAssignment: async (schoolId, id) => {
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    try {
      const res = await studentFeeAssignmentsApi.deleteAssignment(schoolId, id);
      return { success: true, message: res?.message || 'Student fee assignment deleted successfully.' };
    } catch (err) {
      console.warn('API error deleting student fee assignment, deleting locally:', err.message);
      if (mockStudentFeeAssignments[schoolId]) {
        mockStudentFeeAssignments[schoolId] = mockStudentFeeAssignments[schoolId].filter((item) => item.id !== id);
      }
      return { success: true, message: 'Student fee assignment deleted successfully.' };
    }
  },

  assignFees: async (schoolId, { feeStructureIds, targetType, targetIds: _targetIds }) => {
    await delay(MOCK_DELAY.LONG);
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    if (!feeStructureIds?.length) throw { status: 400, message: 'Select at least one fee structure' };

    return {
      success: true,
      message: `Successfully assigned ${feeStructureIds.length} fees to selected ${targetType}.`,
    };
  },

  // ── Fee Collection ──────────────────────────────────────────

  getPendingFees: async (schoolId, filters = {}) => {
    await delay();
    if (!schoolId) throw { status: 400, message: 'School ID is required' };

    let fees = mockPendingFees[schoolId] || [];

    if (filters.studentId) {
      fees = fees.filter((f) => f.studentId === filters.studentId);
    }
    if (filters.status) {
      fees = fees.filter((f) => f.status === filters.status);
    }

    return { success: true, data: fees };
  },

  collectFee: async (schoolId, collectionData) => {
    await delay(MOCK_DELAY.LONG);
    if (!schoolId) throw { status: 400, message: 'School ID is required' };

    const { pendingFeeId, amountPaid, paymentMethod, remarks, collectedBy } = collectionData;

    const pendingFeesList = mockPendingFees[schoolId] || [];
    const feeIndex = pendingFeesList.findIndex((f) => f.id === pendingFeeId);

    if (feeIndex === -1) {
      throw { status: 404, message: 'Pending fee record not found' };
    }

    const pendingFee = pendingFeesList[feeIndex];
    pendingFeesList.splice(feeIndex, 1);

    const txnId = generateId('txn-');
    const receiptNumber = `RCPT-${new Date().getFullYear()}-${Math.floor(Math.random() * 900000 + 100000)}`;

    const transaction = {
      id: txnId,
      receiptNumber,
      studentId: pendingFee.studentId,
      studentName: pendingFee.studentName,
      classId: pendingFee.classId,
      className: pendingFee.className,
      sectionName: pendingFee.sectionName,
      feeHeadId: pendingFee.feeHeadId,
      feeHeadName: pendingFee.feeHeadName,
      amount: amountPaid,
      paymentMethod,
      collectedBy: collectedBy || 'System',
      status: 'success',
      date: new Date().toISOString(),
    };

    if (!mockTransactions[schoolId]) mockTransactions[schoolId] = [];
    mockTransactions[schoolId].unshift(transaction);

    const receipt = {
      ...transaction,
      id: generateId('rcpt-'),
      transactionId: txnId,
      amountInWords: numberToWords(amountPaid) + ' Only',
      remarks,
    };

    if (!mockReceipts[schoolId]) mockReceipts[schoolId] = [];
    mockReceipts[schoolId].unshift(receipt);

    return {
      success: true,
      data: {
        transaction,
        receipt,
      },
      message: 'Payment collected successfully.',
    };
  },

  // ── Transactions & Receipts ─────────────────────────────────

  getTransactions: async (schoolId) => {
    await delay();
    if (!schoolId) throw { status: 400, message: 'School ID is required' };

    return { success: true, data: mockTransactions[schoolId] || [] };
  },

  getReceipt: async (schoolId, receiptId) => {
    await delay();
    if (!schoolId) throw { status: 400, message: 'School ID is required' };

    const receipt = (mockReceipts[schoolId] || []).find((r) => r.id === receiptId);

    if (!receipt) throw { status: 404, message: 'Receipt not found' };

    return { success: true, data: receipt };
  },

  // ── Dashboard Overview Analytics ─────────────────────────────
  getFeeDashboardOverview: async (schoolId, params = {}) => {
    if (!schoolId) throw { status: 400, message: 'School ID is required' };
    return await feeDashboardApi.getDashboardOverview(schoolId, params);
  },
};
